<template>
    <div class="help__row">
        <div class="help__icon">
            <component :is="props.itemData.iconComponent" />
        </div>
        <div class="help__content">
            <h5 class="help__title">{{ props.itemData.title }}</h5>
            <h6 class="help__description">{{ props.itemData.description }}</h6>
            <div class="help-instructions">
                <div class="help-instructions__item">
                    <div class="help-instructions__item-title">
                        1. {{ props.itemData.switch?.platformInstructions }}
                    </div>
                    <custom-switch ref="switch" v-model="platformSwitch" :options="switchOptions" />
                </div>
                <div class="help-instructions__item">
                    <div class="help-instructions__item-title">
                        2. {{ props.itemData.switch?.qrCodeInstructions }}
                    </div>
                    <qrcode-vue class="qr-code-medium qr-content__img" :value="qrCodeLink" :level="'H'" render-as="svg" />
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { ref, type Ref, computed, type PropType, onBeforeUnmount, watch } from "vue";
    import { type IHelpItem } from '../types/help';
    import { type ISwitch } from "@/components/Switch/types";
    import QrcodeVue from 'qrcode.vue';
    import { encrypt } from "@ca.vmedia.tv/domains";
    import { TimeUtils } from '@/utils';
    import { storeToRefs } from 'pinia'
    import { useAccountStore } from "@/stores/Account";

    const AccountStoreModule = useAccountStore();
    const { GetHelpQrExpiresDate } = AccountStoreModule;
    const {
        getHelpQrExpiresDate
    } = storeToRefs(AccountStoreModule);

    interface IPayload {
        StbMacAddress: string;
        sessionId: string;
        SiteID: number | string;
        Box_AppVersion: string;
        Box_BuildNumber: string;
        Box_SysVersion: string;
        userId: string;
    }

    const props = defineProps({
        itemData: {
            type: Object as PropType<IHelpItem>,
            required: true,
        }
    })
    const { siteId, appVersion, buildCounter } = useAppConfig();
    const { $domains, $platform } = useNuxtApp();
    const platformSwitch: Ref<boolean> = ref(false);
    const payload: Ref<IPayload> = ref({
        StbMacAddress: '',
        sessionId: '',
        SiteID: '',
        Box_AppVersion: '',
        Box_BuildNumber: '',
        Box_SysVersion: '',
        userId: '',
    });
    const handle: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const qrLinkExpiresAt = computed((): number | null => getHelpQrExpiresDate.value ? TimeUtils.extractTimestamp(getHelpQrExpiresDate.value) : null);
    const encryptedString = computed((): string => {
        const params = new URLSearchParams();
        Object.entries(payload.value).forEach(([key, value]) => {
            params.append(key, value);
        });
        return encrypt(params.toString());
    })
    const qrCodeLink = computed(() => {
        const android = 'https://fzapp.vmedia.ca/media/contactUs?';
        const ios = 'https://fzapp.vmedia.ca/media/apple/contactUs?';
        const queryString = encrypt(payload.value.userId);
        const url = platformSwitch.value ? ios : android;
        return `${url}${queryString}`;
    })
    const switchOptions: ISwitch[] = [
        {
            name: "Android",
            value: false,
        },
        {
            name: "Apple",
            value: true,
        },
    ]
    const updateLink = (): void => {
        handle.value = setInterval(() => {
            const timeNow = TimeUtils.dateNow();
            if (!qrLinkExpiresAt.value || timeNow >= qrLinkExpiresAt.value) {
                if (handle.value) {
                    clearInterval(handle.value);
                }
                void GetHelpQrExpiresDate({ value: encryptedString.value })
            }
        }, 1000);
    }
    const { StbMacAddress, sessionId, UserSystemID } = await $domains.loadUserInfo();
    const { model } = await $platform.getInfo();
    payload.value = {
        StbMacAddress: StbMacAddress ?? '',
        sessionId: sessionId ?? '',
        SiteID: siteId,
        Box_AppVersion: appVersion,
        Box_BuildNumber: buildCounter,
        Box_SysVersion: model,
        userId: UserSystemID ?? ''
    };
    updateLink();
    onBeforeUnmount(() => {
        if (handle.value) {
            clearInterval(handle.value);
        }
    })
    watch(qrLinkExpiresAt, () => {
        updateLink();
    })
</script>
